import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  BarChart3,
  Briefcase,
  Radio,
  Sparkles,
  CreditCard,
  ShieldCheck,
  Cpu,
  LogOut,
  Zap,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fmtUSDExact } from "@/lib/format";

function trialTimeLeft(proUntil) {
  if (!proUntil) return null;
  const ms = new Date(proUntil).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  return { days, hours, ms };
}

function TrialBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("trialBannerDismissed") === "1",
  );

  if (!user?.on_trial) return null;
  const left = trialTimeLeft(user?.pro_until);
  // Only nudge in the final 3 days of the trial.
  if (!left || left.days > 2) return null;
  if (dismissed) return null;

  const countdown =
    left.days >= 1
      ? `${left.days} day${left.days === 1 ? "" : "s"} ${left.hours}h`
      : `${left.hours}h`;

  return (
    <div
      data-testid="trial-countdown-banner"
      className="relative flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-b border-amber-400/40 bg-amber-400/10 px-6 py-3 text-amber-300"
    >
      <Zap className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
      <span className="font-mono text-[11px] sm:text-xs uppercase tracking-widest">
        Your Pro trial ends in{" "}
        <span className="text-amber-200 font-bold" data-testid="trial-countdown-value">
          {countdown}
        </span>{" "}
        — subscribe to keep the lower trading fees
      </span>
      <button
        data-testid="trial-banner-subscribe-btn"
        onClick={() => nav("/app/billing")}
        className="px-4 py-1.5 bg-amber-400 text-black font-mono uppercase tracking-widest text-[10px] hover:opacity-90 transition-opacity"
      >
        Subscribe now
      </button>
      <button
        data-testid="trial-banner-dismiss-btn"
        onClick={() => {
          sessionStorage.setItem("trialBannerDismissed", "1");
          setDismissed(true);
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-400/70 hover:text-amber-200 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const items = [
    { to: "/app/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/markets", label: "Markets", icon: BarChart3, testid: "nav-markets" },
    { to: "/app/portfolio", label: "Portfolio", icon: Briefcase, testid: "nav-portfolio" },
    { to: "/app/trading", label: "Real Trading", icon: Radio, testid: "nav-trading" },
    { to: "/app/ai", label: "AI Copilot", icon: Sparkles, testid: "nav-ai" },
    { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  ];
  if (user?.is_admin) {
    items.push({ to: "/app/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" });
    items.push({ to: "/app/admin/providers", label: "Providers", icon: Cpu, testid: "nav-providers" });
  }

  return (
    <div className="min-h-screen bg-neo-bg text-white flex" data-testid="app-shell">
      <aside className="w-64 flex-shrink-0 border-r border-neo-border sticky top-0 h-screen flex flex-col">
        <div className="px-6 py-6 border-b border-neo-border">
          <div className="font-mono uppercase tracking-[0.28em] text-sm">Neo.Invest</div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // terminal
          </div>
        </div>

        <div className="px-6 py-6 border-b border-neo-border" data-testid="sidebar-balance">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Cash balance
          </div>
          <div className="mt-2 font-mono text-2xl font-bold tracking-tighter" data-testid="sidebar-cash-value">
            {fmtUSDExact(user?.cash || 0)}
          </div>
        </div>

        <nav className="flex-1 py-4">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 font-mono uppercase tracking-widest text-xs border-l-2 transition-colors ${
                  isActive
                    ? "border-white text-white bg-white/5"
                    : "border-transparent text-neutral-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <it.icon className="w-4 h-4" strokeWidth={1.5} />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-neo-border p-4">
          <div className="text-xs text-neutral-400 font-mono truncate" data-testid="sidebar-user-email">
            {user?.email}
          </div>
          <button
            data-testid="sidebar-logout-button"
            onClick={() => {
              logout();
              nav("/");
            }}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-neo-border py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-3 h-3" strokeWidth={1.5} />
            Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <TrialBanner />
        <Outlet />
      </main>
    </div>
  );
}
